import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { X } from 'lucide-react';
import UserSearchSelect from './UserSearchSelect';

const TaskForm = ({ onSubmit, onCancel, initialData, users = [] }) => {
    const { user } = useAuth();
    const isDG = user?.roles?.some(r => r === 'DG' || r === 'DG_OFFICE');
    const isCoordinator = user?.roles?.some(r => r.startsWith('COORDINATOR_') || r === 'COORDINATOR_RIS');
    const isEmployee = !isDG && !isCoordinator;

    const [formData, setFormData] = useState(initialData || {
        title: '',
        taskType: '',
        subTaskType: '',
        startDate: new Date().toISOString().split('T')[0], // Default today
        endDate: '',
        priority: 'Normal',
        status: 'New', // Default status
        assigneeId: isEmployee ? (user?.sub || user?.username) : '', // Default to self for Employee
        showOnCalendar: null, // Force user to choose
        thematicSkills: [],
        areaSkills: [],
        specialSkills: []
    });

    // Manual State Tracking
    const [isManualTaskType, setIsManualTaskType] = useState(false);
    const [isManualSubType, setIsManualSubType] = useState(false);

    // Predefined subtask checklist state
    const [predefinedSubtasks, setPredefinedSubtasks] = useState({});

    const [loading, setLoading] = useState(false);
    const [attachmentsFiles, setAttachmentsFiles] = useState([]);
    const [attachmentsCategory, setAttachmentsCategory] = useState('PUBLICATION');

    // Initial check for Employee default assignment if not creating new
    useEffect(() => {
        if (!initialData && isEmployee && !formData.assigneeId) {
            setFormData(prev => ({ ...prev, assigneeId: user?.sub || user?.username }));
        }
    }, [initialData, isEmployee, user, formData.assigneeId]);

    // Task Type Categories
    const TASK_TYPES = {
        "Publication": ["Journals", "Books/Report", "Discussion Papers", "Occasional Papers", "Policy Briefs", "Blogs", "Newsletters", "RIS Diary", "RIS Annual Report"],
        "Research": ["Data Analysis", "Field Survey", "Literature Review", "Report Writing"],
        "Governance": ["Committee Meeting", "Board Review", "Compliance"],
        "Admin": ["HR", "Finance", "IT Support"],
        "Pre Event": ["Venue Booking", "Agenda Setting", "Speaker Confirmation", "MEA Approval", "List of Speakers", "Concept Note", "Concept Note Approval", "Concept Note Finalization", "Concept Note Distribution"],
        "Post Event": ["Transcript Upload", "Report Writing", "Follow-up", "Letters of Thanks", "Social Media Updates"]
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Special handling for assigneeId to capture division
        if (name === "assigneeId") {
            const selectedUser = users.find(u => (u.sub || u.UserId || u.Username) === value);
            // Try to find division from Division field or Attributes
            const division = selectedUser?.Division || selectedUser?.Attributes?.find(a => a.Name === 'custom:division')?.Value;

            setFormData(prev => ({
                ...prev,
                [name]: value,
                assigneeDivision: division // Store division
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleTaskTypeChange = (e) => {
        const value = e.target.value;
        setPredefinedSubtasks({}); // Reset checklist state on task type change
        if (value === 'Manual') {
            setIsManualTaskType(true);
            setFormData(prev => ({ ...prev, taskType: '', subTaskType: '' }));
            setIsManualSubType(true); // If main is manual, sub implies manual input or empty
        } else {
            setIsManualTaskType(false);
            setFormData(prev => ({ ...prev, taskType: value, subTaskType: '' }));
            setIsManualSubType(false);
        }
    };

    const handleSubTypeChange = (e) => {
        const value = e.target.value;
        if (value === 'Manual') {
            setIsManualSubType(true);
            setFormData(prev => ({ ...prev, subTaskType: '' }));
        } else {
            setIsManualSubType(false);
            setFormData(prev => ({ ...prev, subTaskType: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        // Logic: "If User doesnot type anything... by default Other will be choosen"
        // Logic: "if user chooses Publication but doesnot choose anything in subtask type then Other will be choosen"
        let finalTaskType = formData.taskType;
        let finalSubType = formData.subTaskType;

        if (!finalTaskType || finalTaskType.trim() === '') {
            finalTaskType = 'Other';
        }
        if (!finalSubType || finalSubType.trim() === '') {
            // Note: If TaskType is "Other" naturally, SubType might also be "Other" or empty.
            // Requirement says checks both fields.
            finalSubType = 'Other';
        }

        if (formData.showOnCalendar === null) {
            alert('Please select whether to show this task on the calendar.');
            setLoading(false);
            return;
        }

        const autoSubtasks = Object.entries(predefinedSubtasks)
            .filter(([_, data]) => data.selected)
            .map(([name, data]) => ({ name, ...data }));

        const payload = {
            ...formData,
            taskType: finalTaskType,
            subTaskType: finalSubType,
            showOnCalendar: formData.showOnCalendar === 'yes',
            attachmentsFiles,
            attachmentsCategory,
            predefinedSubtasks: autoSubtasks.length > 0 ? autoSubtasks : undefined
        };

        try {
            await onSubmit(payload);
        } finally {
            setLoading(false);
        }
    };

    const getPriorityColor = (p) => {
        switch (p) {
            case 'High': return '#dc3545'; // Red
            case 'Immediate': return '#fd7e14'; // Orange
            case 'Low': return '#28a745'; // Green
            default: return '#333';
        }
    };

    return (
        <form onSubmit={handleSubmit} style={{
            display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '900px',
            background: 'linear-gradient(155deg, #dbeafe 0%, #ede9fe 42%, #ccfbf1 100%)', // Match Sidebar gradient
            padding: '2.5rem', borderRadius: '16px', border: '1px solid #c7d2fe',
            boxShadow: '0 12px 35px rgba(30, 41, 59, 0.1)',
            color: '#1e293b'
        }}>
            <div style={{ paddingBottom: '1rem', borderBottom: '1px solid rgba(0,0,0,0.05)', marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <h3 style={{ margin: '0 0 0.25rem 0', color: '#1e3a8a', fontSize: '1.4rem', fontWeight: 700 }}>{initialData ? 'Edit Task' : 'Create New Task'}</h3>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>Provide the details below to organize your task.</p>
                </div>
                <button
                    type="button"
                    onClick={onCancel}
                    style={{
                        background: 'rgba(255, 255, 255, 0.5)',
                        border: 'none',
                        borderRadius: '50%',
                        padding: '0.5rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background 0.2s',
                        color: '#64748b'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)'}
                >
                    <X size={20} />
                </button>
            </div>

            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Task Title</label>
                <input
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    placeholder="Enter task title"
                    style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', transition: 'border-color 0.2s' }}
                />
            </div>

            {/* Row 2: Type, Sub Type, Priority */}
            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '1.5rem', alignItems: 'flex-start' }}>
                {/* Task Type */}
                <div style={{ gridColumn: 'span 5' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Task Type</label>
                    {!isManualTaskType ? (
                        <select
                            value={formData.taskType}
                            onChange={handleTaskTypeChange}
                            style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', background: 'white', fontSize: '0.95rem' }}
                        >
                            <option value="">Select Type...</option>
                            {Object.keys(TASK_TYPES).map(t => <option key={t} value={t}>{t}</option>)}
                            <option value="Manual">Manual Entry</option>
                        </select>
                    ) : (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <input
                                value={formData.taskType}
                                onChange={handleChange}
                                name="taskType"
                                placeholder="Enter Type"
                                style={{ flex: 1, padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }}
                            />
                            <button type="button" onClick={() => setIsManualTaskType(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#dc3545' }}>✕</button>
                        </div>
                    )}
                </div>

                {/* Sub Task Type */}
                <div style={{ gridColumn: 'span 5' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Task Sub Type</label>
                    {formData.taskType === 'Pre Event' || formData.taskType === 'Post Event' ? (
                        <div style={{
                            padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1',
                            background: '#f8fafc', fontSize: '0.95rem', color: '#64748b'
                        }}>
                            Predefined Checklist Selected (See Below)
                        </div>
                    ) : (isManualTaskType || isManualSubType) ? (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <input
                                value={formData.subTaskType}
                                onChange={handleChange}
                                name="subTaskType"
                                placeholder="Enter Task Sub Type"
                                style={{ flex: 1, padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }}
                            />
                            {!isManualTaskType && (
                                <button type="button" onClick={() => setIsManualSubType(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#dc3545' }}>✕</button>
                            )}
                        </div>
                    ) : (
                        <select
                            value={formData.subTaskType}
                            onChange={handleSubTypeChange}
                            disabled={!formData.taskType}
                            style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', background: 'white', opacity: !formData.taskType ? 0.6 : 1, fontSize: '0.95rem' }}
                        >
                            <option value="">Select Task Sub Type...</option>
                            {formData.taskType && TASK_TYPES[formData.taskType]?.map(st => (
                                <option key={st} value={st}>{st}</option>
                            ))}
                            <option value="Manual">Manual Entry</option>
                        </select>
                    )}
                </div>

                {/* Priority */}
                <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Priority</label>
                    <select
                        name="priority"
                        value={formData.priority}
                        onChange={handleChange}
                        style={{
                            width: '100%', padding: '0.7rem', borderRadius: '8px',
                            border: `1px solid ${getPriorityColor(formData.priority)}`,
                            color: getPriorityColor(formData.priority),
                            fontWeight: 'bold',
                            background: 'white',
                            fontSize: '0.95rem'
                        }}
                    >
                        <option value="High" style={{ color: '#dc3545' }}>High</option>
                        <option value="Normal" style={{ color: '#333' }}>Normal</option>
                        <option value="Low" style={{ color: '#28a745' }}>Low</option>
                        <option value="Immediate" style={{ color: '#fd7e14' }}>Immediate</option>
                    </select>
                </div>
            </div>

            {/* Checklist for Pre Event / Post Event */}
            {(formData.taskType === 'Pre Event' || formData.taskType === 'Post Event') && TASK_TYPES[formData.taskType] && (
                <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                    <label style={{ display: 'block', marginBottom: '1rem', fontWeight: 600, fontSize: '1.05rem', color: '#1e3a8a' }}>{formData.taskType} Checklist</label>
                    <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', color: '#64748b' }}>Select the items below to automatically create them as subtasks with specific assignees and due dates.</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                        {TASK_TYPES[formData.taskType].map(item => {
                            const isChecked = !!predefinedSubtasks[item]?.selected;
                            const assignee = predefinedSubtasks[item]?.assigneeId || formData.assigneeId || '';
                            const subtaskEndDate = predefinedSubtasks[item]?.endDate || formData.endDate || '';

                            return (
                                <div key={item} style={{
                                    padding: '1rem', background: isChecked ? '#f0f9ff' : '#f8fafc',
                                    border: `1px solid ${isChecked ? '#bae6fd' : '#e2e8f0'}`,
                                    borderRadius: '8px', transition: 'all 0.2s', display: 'flex', flexDirection: 'column', gap: '0.75rem'
                                }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', fontWeight: 600, color: isChecked ? '#0369a1' : '#334155' }}>
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={(e) => setPredefinedSubtasks(prev => ({
                                                ...prev,
                                                [item]: {
                                                    ...prev[item],
                                                    selected: e.target.checked,
                                                    assigneeId: prev[item]?.assigneeId || formData.assigneeId,
                                                    endDate: prev[item]?.endDate || formData.endDate
                                                }
                                            }))}
                                            style={{ width: '1.1rem', height: '1.1rem' }}
                                        />
                                        {item}
                                    </label>

                                    {isChecked && (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginLeft: '1.85rem' }}>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '0.25rem' }}>Assignee</label>
                                                <UserSearchSelect
                                                    users={users}
                                                    value={assignee}
                                                    onChange={(id) => setPredefinedSubtasks(prev => ({
                                                        ...prev,
                                                        [item]: { ...prev[item], assigneeId: id }
                                                    }))}
                                                    placeholder={isEmployee ? 'Me' : 'Search assignee…'}
                                                    isEmployee={isEmployee}
                                                    currentUser={user}
                                                    compact
                                                />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '0.25rem' }}>Due Date</label>
                                                <input
                                                    type="date"
                                                    value={subtaskEndDate}
                                                    onChange={(e) => setPredefinedSubtasks(prev => ({
                                                        ...prev,
                                                        [item]: { ...prev[item], endDate: e.target.value }
                                                    }))}
                                                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Row 3: Dates */}
            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '1.5rem' }}>
                <div style={{ gridColumn: 'span 6' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Start Date</label>
                    <input
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleChange}
                        required
                        style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }}
                    />
                </div>
                <div style={{ gridColumn: 'span 6' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>End Date</label>
                    <input
                        type="date"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleChange}
                        required
                        style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }}
                    />
                </div>
            </div>

            {/* Row 4: Skills & Assignee */}
            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', display: 'grid', gap: '1.5rem', gridTemplateColumns: '1fr 1fr' }}>
                {/* Skills Input */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Required Skills</label>

                    {/* Thematic Skills Input */}
                    <div style={{ position: 'relative' }}>
                        <label style={{ display: 'block', marginBottom: '0.2rem', fontSize: '0.8rem', color: '#64748b' }}>Thematic Specializations</label>
                        <div style={{
                            border: '1px solid #cbd5e1', borderRadius: '8px', padding: '0.5rem',
                            background: '#f8fafc', minHeight: '42px', display: 'flex', flexWrap: 'wrap', gap: '0.5rem'
                        }}>
                            {formData.thematicSkills && formData.thematicSkills.map(skill => (
                                <span key={skill} style={{
                                    background: '#e0f2fe', color: '#0369a1', fontSize: '0.8rem', fontWeight: 600,
                                    padding: '2px 10px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #bae6fd'
                                }}>
                                    {skill}
                                    <button type="button" onClick={() => {
                                        setFormData(prev => ({ ...prev, thematicSkills: prev.thematicSkills.filter(s => s !== skill) }));
                                    }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#0369a1', padding: 0, fontSize: '1.1rem', lineHeight: 1 }}>&times;</button>
                                </span>
                            ))}
                            <input
                                placeholder={formData.thematicSkills?.length > 0 ? "" : "Search thematic..."}
                                list="thematic-suggestions"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        const val = e.target.value.trim();
                                        if (val && (!formData.thematicSkills || !formData.thematicSkills.includes(val))) {
                                            setFormData(prev => ({ ...prev, thematicSkills: [...(prev.thematicSkills || []), val] }));
                                            e.target.value = '';
                                        }
                                    }
                                }}
                                style={{ border: 'none', outline: 'none', flex: 1, minWidth: '100px', fontSize: '0.95rem', background: 'transparent' }}
                            />
                            <datalist id="thematic-suggestions">
                                {Array.from(new Set(users.flatMap(u => u.ThematicSpecialization || []))).sort().map(tag => (
                                    <option key={tag} value={tag} />
                                ))}
                            </datalist>
                        </div>
                    </div>

                    {/* Area Skills Input */}
                    <div style={{ position: 'relative' }}>
                        <label style={{ display: 'block', marginBottom: '0.2rem', fontSize: '0.8rem', color: '#64748b' }}>Area Specializations</label>
                        <div style={{
                            border: '1px solid #cbd5e1', borderRadius: '8px', padding: '0.5rem',
                            background: '#f8fafc', minHeight: '42px', display: 'flex', flexWrap: 'wrap', gap: '0.5rem'
                        }}>
                            {formData.areaSkills && formData.areaSkills.map(skill => (
                                <span key={skill} style={{
                                    background: '#ecfdf5', color: '#047857', fontSize: '0.8rem', fontWeight: 600,
                                    padding: '2px 10px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #a7f3d0'
                                }}>
                                    {skill}
                                    <button type="button" onClick={() => {
                                        setFormData(prev => ({ ...prev, areaSkills: prev.areaSkills.filter(s => s !== skill) }));
                                    }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#047857', padding: 0, fontSize: '1.1rem', lineHeight: 1 }}>&times;</button>
                                </span>
                            ))}
                            <input
                                placeholder={formData.areaSkills?.length > 0 ? "" : "Search area..."}
                                list="area-suggestions"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        const val = e.target.value.trim();
                                        if (val && (!formData.areaSkills || !formData.areaSkills.includes(val))) {
                                            setFormData(prev => ({ ...prev, areaSkills: [...(prev.areaSkills || []), val] }));
                                            e.target.value = '';
                                        }
                                    }
                                }}
                                style={{ border: 'none', outline: 'none', flex: 1, minWidth: '100px', fontSize: '0.95rem', background: 'transparent' }}
                            />
                            <datalist id="area-suggestions">
                                {Array.from(new Set(users.flatMap(u => u.AreaSpecialization || []))).sort().map(tag => (
                                    <option key={tag} value={tag} />
                                ))}
                            </datalist>
                        </div>
                    </div>

                    {/* Special Skills Input */}
                    <div style={{ position: 'relative' }}>
                        <label style={{ display: 'block', marginBottom: '0.2rem', fontSize: '0.8rem', color: '#64748b' }}>Special Skills</label>
                        <div style={{
                            border: '1px solid #cbd5e1', borderRadius: '8px', padding: '0.5rem',
                            background: '#f8fafc', minHeight: '42px', display: 'flex', flexWrap: 'wrap', gap: '0.5rem'
                        }}>
                            {formData.specialSkills && formData.specialSkills.map(skill => (
                                <span key={skill} style={{
                                    background: '#fef3c7', color: '#b45309', fontSize: '0.8rem', fontWeight: 600,
                                    padding: '2px 10px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #fde68a'
                                }}>
                                    {skill}
                                    <button type="button" onClick={() => {
                                        setFormData(prev => ({ ...prev, specialSkills: prev.specialSkills.filter(s => s !== skill) }));
                                    }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#b45309', padding: 0, fontSize: '1.1rem', lineHeight: 1 }}>&times;</button>
                                </span>
                            ))}
                            <input
                                placeholder={formData.specialSkills?.length > 0 ? "" : "Search special skills..."}
                                list="special-suggestions"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        const val = e.target.value.trim();
                                        if (val && (!formData.specialSkills || !formData.specialSkills.includes(val))) {
                                            setFormData(prev => ({ ...prev, specialSkills: [...(prev.specialSkills || []), val] }));
                                            e.target.value = '';
                                        }
                                    }
                                }}
                                style={{ border: 'none', outline: 'none', flex: 1, minWidth: '100px', fontSize: '0.95rem', background: 'transparent' }}
                            />
                            <datalist id="special-suggestions">
                                {Array.from(new Set(users.flatMap(u => u.SpecialSkills || []))).sort().map(tag => (
                                    <option key={tag} value={tag} />
                                ))}
                            </datalist>
                        </div>
                        <small style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>Press Enter to add a skill</small>
                    </div>
                </div>

                {/* Assignee */}
                <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Assigned To</label>
                    <UserSearchSelect
                        users={users}
                        value={formData.assigneeId}
                        onChange={(id, selectedUser) => {
                            let tCoordId = null;
                            const division = selectedUser?.Division || selectedUser?.Attributes?.find(a => a.Name === 'custom:division')?.Value;

                            if (selectedUser?._isExternal) {
                                const targetDiv = (division || '').toLowerCase().trim();
                                const coord = users.find(x => {
                                    const xDiv = (x.Division || x.Attributes?.find(a => a.Name === 'custom:division')?.Value || '').toLowerCase().trim();
                                    return xDiv === targetDiv && x.Groups?.some(g => g.startsWith('COORDINATOR_'));
                                });
                                tCoordId = coord ? (coord.UserId || coord.sub || coord.Username) : null;
                            }

                            setFormData(prev => ({
                                ...prev,
                                assigneeId: id,
                                assigneeDivision: division,
                                isCrossDivision: !!selectedUser?._isExternal,
                                targetCoordinatorId: tCoordId
                            }));
                        }}
                        placeholder="Search by name or email…"
                        isEmployee={isEmployee}
                        currentUser={user}
                        requiredSkills={[...(formData.thematicSkills || []), ...(formData.areaSkills || []), ...(formData.specialSkills || [])]}
                    />
                </div>
            </div>

            {/* Row 5: Calendar Settings */}
            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>
                    Show this Task on the Calendar? <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                            type="radio"
                            name="showOnCalendar"
                            value="yes"
                            checked={formData.showOnCalendar === 'yes'}
                            onChange={handleChange}
                            style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.95rem', color: '#1e293b' }}>Yes, show on calendar</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                            type="radio"
                            name="showOnCalendar"
                            value="no"
                            checked={formData.showOnCalendar === 'no'}
                            onChange={handleChange}
                            style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.95rem', color: '#1e293b' }}>No, hide from calendar</span>
                    </label>
                </div>
            </div>

            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>
                    Attach Files (optional)
                </label>
                <select
                    value={attachmentsCategory}
                    onChange={(e) => setAttachmentsCategory(e.target.value)}
                    style={{ width: '100%', marginBottom: '0.75rem', padding: '0.7rem', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc', fontSize: '0.95rem' }}
                >
                    <option value="PUBLICATION">Publication (General)</option>
                    <option value="PUBLICATION_JOURNALS">Publication - Journals</option>
                    <option value="PUBLICATION_BOOKS_REPORT">Publication - Books/Report</option>
                    <option value="PUBLICATION_DISCUSSION_PAPERS">Publication - Discussion Papers</option>
                    <option value="PUBLICATION_OCCASIONAL_PAPERS">Publication - Occasional Papers</option>
                    <option value="PUBLICATION_POLICY_BRIEFS">Publication - Policy Briefs</option>
                    <option value="PUBLICATION_BLOGS">Publication - Blogs</option>
                    <option value="PUBLICATION_NEWSLETTERS">Publication - Newsletters</option>
                    <option value="PUBLICATION_RIS_DIARY">Publication - RIS Diary</option>
                    <option value="PUBLICATION_RIS_ANNUAL_REPORT">Publication - RIS Annual Report</option>
                    <option value="RESEARCH">Research (General)</option>
                    <option value="RESEARCH_DATA_ANALYSIS">Research - Data Analysis</option>
                    <option value="RESEARCH_FIELD_SURVEY">Research - Field Survey</option>
                    <option value="RESEARCH_LITERATURE_REVIEW">Research - Literature Review</option>
                    <option value="RESEARCH_REPORT_WRITING">Research - Report Writing</option>
                    <option value="DRAFT">Draft</option>
                    <option value="IT">IT</option>
                    <option value="ADMIN">Admin</option>
                    <option value="OTHER">Other</option>
                </select>
                <input
                    type="file"
                    multiple
                    onChange={(e) => setAttachmentsFiles(Array.from(e.target.files || []))}
                    style={{ width: '100%', fontSize: '0.9rem' }}
                />
                {attachmentsFiles.length > 0 && (
                    <p style={{ margin: '0.5rem 0 0', color: '#0369a1', fontSize: '0.85rem', fontWeight: 600 }}>
                        {attachmentsFiles.length} file(s) selected under {attachmentsCategory.replace(/_/g, ' ')}
                    </p>
                )}
            </div>

            {/* Status is HIDDEN on Creation - Defaults to New */}
            {initialData && (
                <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Status</label>
                    <select
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        className="modern-dropdown"
                    >
                        <option value="New">New</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                        <option value="Cancelled">Cancelled</option>
                    </select>
                </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
                <button
                    type="button"
                    onClick={onCancel}
                    style={{
                        padding: '0.75rem 2rem', background: 'white', color: '#475569', fontWeight: 600,
                        border: '1px solid #cbd5e1', borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={loading}
                    style={{
                        padding: '0.75rem 2.5rem',
                        background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                        color: 'white', fontWeight: 700,
                        border: 'none', borderRadius: '12px', cursor: 'pointer',
                        opacity: loading ? 0.7 : 1,
                        boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                        transition: 'transform 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                    {loading ? 'Saving...' : (initialData ? 'Update Task' : 'Create Task')}
                </button>
            </div>
        </form >
    );
};

export default TaskForm;
